import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Camera, MapPin, LogIn, LogOut, CheckCircle2, AlertCircle, History, User as UserIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EmployeeAttendance() {
  const [user, setUser] = useState(null);
  const [todayStatus, setTodayStatus] = useState({ check_in: null, check_out: null });
  const [showCamera, setShowCamera] = useState(false);
  const [attendanceType, setAttendanceType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recentRecords, setRecentRecords] = useState([]);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (!token || !userData) {
      navigate('/employee/login');
      return;
    }
    setUser(JSON.parse(userData));
    fetchTodayStatus();
    fetchRecentRecords();
  }, [navigate]);

  const fetchTodayStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/attendance/today`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTodayStatus(response.data);
    } catch (error) {
      console.error('Error fetching today status:', error);
    }
  };

  const fetchRecentRecords = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/attendance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecentRecords(response.data.slice(0, 5));
    } catch (error) {
      console.error('Error fetching records:', error);
    }
  };

  const startCamera = async (type) => {
    setAttendanceType(type);
    setShowCamera(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      toast.error('Camera access denied. Please allow camera permission.');
      setShowCamera(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    setShowCamera(false);
    setAttendanceType(null);
  };

  const captureAndSubmit = async () => {
    setLoading(true);
    try {
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(videoRef.current, 0, 0);
      
      // Check image brightness
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      let totalBrightness = 0;
      for (let i = 0; i < data.length; i += 4) {
        const brightness = (data[i] + data[i + 1] + data[i + 2]) / 3;
        totalBrightness += brightness;
      }
      const avgBrightness = totalBrightness / (data.length / 4);
      
      if (avgBrightness < 40) {
        toast.error('Photo is too dark. Please ensure good lighting and try again.');
        setLoading(false);
        return;
      }

      const selfieData = canvas.toDataURL('image/jpeg', 0.7);

      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        });
      });

      const token = localStorage.getItem('token');
      await axios.post(`${API}/attendance`, {
        employee_id: user.id,
        attendance_type: attendanceType,
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        selfie_data: selfieData
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      stopCamera();
      toast.success(`${attendanceType === 'check_in' ? 'Check-in' : 'Check-out'} successful!`);
      fetchTodayStatus();
      fetchRecentRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark attendance');
      stopCamera();
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/employee/login');
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-zinc-50 pb-20">
      <div className="bg-white border-b-2 border-zinc-100 p-6">
        <div className="flex items-center justify-between mb-3">
          <img 
            src="/anmol-logo.jpg" 
            alt="ANMOL" 
            className="h-12"
          />
          <button onClick={handleLogout} className="text-sm text-zinc-600 hover:text-zinc-900 font-medium" data-testid="logout-button">
            Logout
          </button>
        </div>
        <div>
          <h1 className="font-heading text-2xl font-bold uppercase tracking-tight text-zinc-900" data-testid="employee-name">{user.name}</h1>
          <p className="font-mono text-sm text-zinc-500" data-testid="employee-id">ID: {user.employee_id}</p>
        </div>
      </div>

      <div className="p-6 space-y-6">
        <div className="bg-white border-2 border-zinc-100 rounded-lg p-6">
          <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-zinc-900 mb-4" data-testid="today-status-title">Today's Status</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${todayStatus.check_in ? 'bg-green-100' : 'bg-zinc-100'}`}>
                <LogIn size={20} className={todayStatus.check_in ? 'text-green-700' : 'text-zinc-400'} />
              </div>
              <div>
                <p className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Check-in</p>
                <p className="font-mono text-sm font-bold text-zinc-900" data-testid="checkin-time">
                  {todayStatus.check_in ? new Date(todayStatus.check_in.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '--:--'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${todayStatus.check_out ? 'bg-green-100' : 'bg-zinc-100'}`}>
                <LogOut size={20} className={todayStatus.check_out ? 'text-green-700' : 'text-zinc-400'} />
              </div>
              <div>
                <p className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Check-out</p>
                <p className="font-mono text-sm font-bold text-zinc-900" data-testid="checkout-time">
                  {todayStatus.check_out ? new Date(todayStatus.check_out.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '--:--'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {!showCamera ? (
          <div className="space-y-4">
            <button
              onClick={() => startCamera('check_in')}
              disabled={!!todayStatus.check_in}
              className="w-full h-64 md:h-80 flex flex-col items-center justify-center gap-4 bg-zinc-900 text-white rounded-lg shadow-xl hover:bg-zinc-800 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="checkin-button"
            >
              <LogIn size={64} strokeWidth={2} />
              <div className="text-center">
                <p className="font-heading text-3xl font-extrabold uppercase tracking-tight">Check In</p>
                <p className="text-sm text-zinc-300 mt-2">Mark your arrival</p>
              </div>
              {todayStatus.check_in && (
                <div className="flex items-center gap-2 bg-green-600 px-4 py-2 rounded-full">
                  <CheckCircle2 size={16} />
                  <span className="text-xs font-bold uppercase">Completed</span>
                </div>
              )}
            </button>

            <button
              onClick={() => startCamera('check_out')}
              disabled={!todayStatus.check_in || !!todayStatus.check_out}
              className="w-full h-64 md:h-80 flex flex-col items-center justify-center gap-4 bg-zinc-900 text-white rounded-lg shadow-xl hover:bg-zinc-800 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="checkout-button"
            >
              <LogOut size={64} strokeWidth={2} />
              <div className="text-center">
                <p className="font-heading text-3xl font-extrabold uppercase tracking-tight">Check Out</p>
                <p className="text-sm text-zinc-300 mt-2">Mark your departure</p>
              </div>
              {todayStatus.check_out && (
                <div className="flex items-center gap-2 bg-green-600 px-4 py-2 rounded-full">
                  <CheckCircle2 size={16} />
                  <span className="text-xs font-bold uppercase">Completed</span>
                </div>
              )}
            </button>
          </div>
        ) : (
          <div className="bg-white border-2 border-zinc-900 rounded-lg overflow-hidden">
            <div className="bg-zinc-900 text-white p-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Camera size={20} />
                <span className="font-heading font-bold uppercase tracking-wider">
                  {attendanceType === 'check_in' ? 'Check In' : 'Check Out'}
                </span>
              </div>
              <button onClick={stopCamera} className="text-sm hover:text-zinc-300" data-testid="cancel-camera-button">
                Cancel
              </button>
            </div>
            <video ref={videoRef} autoPlay playsInline className="w-full" data-testid="camera-video" />
            <div className="p-4 bg-zinc-50">
              <button
                onClick={captureAndSubmit}
                disabled={loading}
                className="w-full h-14 bg-green-600 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-green-700 active:scale-95 transition-all disabled:opacity-50"
                data-testid="capture-submit-button"
              >
                {loading ? 'Processing...' : 'Capture & Submit'}
              </button>
              <p className="text-xs text-zinc-500 text-center mt-3 flex items-center justify-center gap-2">
                <MapPin size={14} />
                Location will be automatically captured
              </p>
            </div>
          </div>
        )}

        {recentRecords.length > 0 && (
          <div className="bg-white border-2 border-zinc-100 rounded-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <History size={20} className="text-zinc-600" />
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-zinc-900" data-testid="recent-history-title">Recent History</h2>
            </div>
            <div className="space-y-3">
              {recentRecords.map((record, idx) => (
                <div key={record.id} className="flex items-center justify-between border-b border-zinc-100 pb-3 last:border-0" data-testid={`history-record-${idx}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${record.status === 'approved' ? 'bg-green-100' : 'bg-red-100'}`}>
                      {record.status === 'approved' ? (
                        <CheckCircle2 size={18} className="text-green-700" />
                      ) : (
                        <AlertCircle size={18} className="text-red-700" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-zinc-900 uppercase">{record.attendance_type.replace('_', '-')}</p>
                      <p className="font-mono text-xs text-zinc-500">{new Date(record.timestamp).toLocaleString()}</p>
                    </div>
                  </div>
                  {record.location_name && (
                    <span className="text-xs text-zinc-600 font-medium">{record.location_name}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
